import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import { FiPlus, FiEdit2, FiTrash2, FiTag } from 'react-icons/fi';

const PLANS = ['basic', 'professional', 'enterprise'];
const CYCLES = ['monthly', 'quarterly', 'yearly'];

export default function AdminPromoCodes() {
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState<any>(getEmptyForm());
  const [saving, setSaving] = useState(false);

  function getEmptyForm() {
    return {
      code: '', discount_type: 'fixed', discount_value: '', max_discount: '',
      applicable_plans: [] as string[], applicable_cycles: [] as string[],
      start_date: '', end_date: '', usage_limit: '', per_user_limit: '1', is_active: true,
    };
  }

  const load = () => {
    api.get('/admin/promo-codes').then(res => { setCodes(res.data); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(getEmptyForm()); setShowModal(true); };
  const openEdit = (p: any) => {
    setEditing(p);
    setForm({
      code: p.code, discount_type: p.discount_type, discount_value: p.discount_value,
      max_discount: p.max_discount || '', applicable_plans: p.applicable_plans || [],
      applicable_cycles: p.applicable_cycles || [],
      start_date: p.start_date ? p.start_date.split('T')[0] : '',
      end_date: p.end_date ? p.end_date.split('T')[0] : '',
      usage_limit: p.usage_limit || '', per_user_limit: p.per_user_limit || '1', is_active: p.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        discount_value: parseFloat(form.discount_value) || 0,
        max_discount: form.max_discount ? parseFloat(form.max_discount) : null,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
        per_user_limit: parseInt(form.per_user_limit) || 1,
        applicable_plans: form.applicable_plans.length ? form.applicable_plans : null,
        applicable_cycles: form.applicable_cycles.length ? form.applicable_cycles : null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      if (editing) {
        await api.put(`/admin/promo-codes/${editing.id}`, payload);
      } else {
        await api.post('/admin/promo-codes', payload);
      }
      setShowModal(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Error saving promo code');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this promo code?')) return;
    await api.delete(`/admin/promo-codes/${id}`);
    load();
  };

  const togglePlan = (plan: string) => {
    setForm((f: any) => ({
      ...f,
      applicable_plans: f.applicable_plans.includes(plan)
        ? f.applicable_plans.filter((p: string) => p !== plan)
        : [...f.applicable_plans, plan],
    }));
  };

  const toggleCycle = (cycle: string) => {
    setForm((f: any) => ({
      ...f,
      applicable_cycles: f.applicable_cycles.includes(cycle)
        ? f.applicable_cycles.filter((c: string) => c !== cycle)
        : [...f.applicable_cycles, cycle],
    }));
  };

  if (loading) return <div className="empty-state"><p>Loading...</p></div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}><FiTag style={{ marginRight: 8 }} />Platform Promo Codes</h1>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus style={{ marginRight: 4 }} /> Add Code</button>
      </div>

      <div className="card">
        <div className="data-table">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Discount</th>
                <th>Plans</th>
                <th>Cycles</th>
                <th>Usage</th>
                <th>Dates</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((p) => (
                <tr key={p.id}>
                  <td><strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{p.code}</strong></td>
                  <td>
                    {p.discount_type === 'percentage'
                      ? `${p.discount_value}%${p.max_discount ? ` (max ${p.max_discount} KD)` : ''}`
                      : `${Number(p.discount_value).toFixed(3)} KD`}
                  </td>
                  <td style={{ fontSize: 12 }}>{p.applicable_plans?.join(', ') || 'All'}</td>
                  <td style={{ fontSize: 12 }}>{p.applicable_cycles?.join(', ') || 'All'}</td>
                  <td>{p.usage_count}{p.usage_limit ? ` / ${p.usage_limit}` : ''}</td>
                  <td style={{ fontSize: 12 }}>
                    {p.start_date ? new Date(p.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-'}
                    {' → '}
                    {p.end_date ? new Date(p.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                  </td>
                  <td>
                    <span style={{
                      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                      background: p.is_active ? '#10b98118' : '#ef444418',
                      color: p.is_active ? '#10b981' : '#ef4444',
                    }}>
                      {p.is_active ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(p)}><FiEdit2 /></button>
                      <button className="btn btn-sm btn-outline" style={{ color: '#ef4444', borderColor: '#ef4444' }} onClick={() => handleDelete(p.id)}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {codes.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--gray-400)' }}>No promo codes yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => !saving && setShowModal(false)}>
          <div style={{ background: 'white', borderRadius: 16, padding: 28, maxWidth: 500, width: '95%', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}>
            <h2 style={{ margin: '0 0 20px', fontSize: 18 }}>{editing ? 'Edit Promo Code' : 'Create Promo Code'}</h2>

            <div style={{ display: 'grid', gap: 14 }}>
              {/* Code */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Code</label>
                <input className="form-input" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. WELCOME50" style={{ fontFamily: 'monospace', textTransform: 'uppercase' }} />
              </div>

              {/* Discount Type + Value */}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Discount Type</label>
                  <select className="form-input" value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })}>
                    <option value="fixed">Fixed (KD)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                    {form.discount_type === 'percentage' ? 'Percentage (%)' : 'Amount (KD)'}
                  </label>
                  <input className="form-input" type="number" step="0.001" value={form.discount_value}
                    onChange={e => setForm({ ...form, discount_value: e.target.value })} />
                </div>
              </div>

              {/* Max Discount (for percentage) */}
              {form.discount_type === 'percentage' && (
                <div>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Max Discount (KD) — optional</label>
                  <input className="form-input" type="number" step="0.001" value={form.max_discount}
                    onChange={e => setForm({ ...form, max_discount: e.target.value })} placeholder="Leave empty for no cap" />
                </div>
              )}

              {/* Applicable Plans */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Applicable Plans (empty = all)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {PLANS.map(plan => (
                    <button key={plan} onClick={() => togglePlan(plan)} style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: form.applicable_plans.includes(plan) ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                      background: form.applicable_plans.includes(plan) ? 'var(--primary-light, #eef2ff)' : 'white',
                      textTransform: 'capitalize',
                    }}>{plan}</button>
                  ))}
                </div>
              </div>

              {/* Applicable Cycles */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>Applicable Cycles (empty = all)</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {CYCLES.map(cycle => (
                    <button key={cycle} onClick={() => toggleCycle(cycle)} style={{
                      padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: form.applicable_cycles.includes(cycle) ? '2px solid var(--primary)' : '2px solid var(--gray-200)',
                      background: form.applicable_cycles.includes(cycle) ? 'var(--primary-light, #eef2ff)' : 'white',
                      textTransform: 'capitalize',
                    }}>{cycle}</button>
                  ))}
                </div>
              </div>

              {/* Usage */}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Total Usage Limit</label>
                  <input className="form-input" type="number" value={form.usage_limit}
                    onChange={e => setForm({ ...form, usage_limit: e.target.value })} placeholder="Unlimited" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Per User Limit</label>
                  <input className="form-input" type="number" value={form.per_user_limit}
                    onChange={e => setForm({ ...form, per_user_limit: e.target.value })} />
                </div>
              </div>

              {/* Dates */}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>Start Date</label>
                  <input className="form-input" type="date" value={form.start_date}
                    onChange={e => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>End Date</label>
                  <input className="form-input" type="date" value={form.end_date}
                    onChange={e => setForm({ ...form, end_date: e.target.value })} />
                </div>
              </div>

              {/* Active */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
                <span style={{ fontSize: 14, fontWeight: 600 }}>Active</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleSave} disabled={saving || !form.code || !form.discount_value}>
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
